import { X } from "lucide-react";
import { useState } from "react";
import { FlowBuilder } from "@/components/flows/FlowBuilder";
import type { Flow } from "@/types";

interface FlowCreateModalProps {
  editingFlow?: Flow | null;
  onSave: (data: {
    name: string;
    description: string;
    purpose: string;
    steps: Array<{
      agent_uuid?: string;
      step_type?: string;
      instructions?: string;
      expected_output?: string;
      config?: Record<string, unknown>;
      requires_review?: boolean;
    }>;
    connectors?: Array<{
      from_step_order?: number;
      to_step_order: number;
      label?: string;
    }>;
  }) => Promise<void>;
  onCancel: () => void;
}

export function FlowCreateModal({ editingFlow, onSave, onCancel }: Readonly<FlowCreateModalProps>) {
  const [step, setStep] = useState<"details" | "builder">(editingFlow ? "builder" : "details");
  const [name, setName] = useState(editingFlow?.name || "");
  const [description, setDescription] = useState(editingFlow?.description || "");
  const [purpose, setPurpose] = useState(editingFlow?.purpose || "");

  const handleNextToBuilder = () => {
    if (!name.trim()) return;
    setStep("builder");
  };

  const handleSaveFlow = async (
    steps: Array<{
      agent_uuid?: string;
      step_type?: string;
      instructions?: string;
      expected_output?: string;
      config?: Record<string, unknown>;
      requires_review?: boolean;
    }>,
    connectors?: Array<{
      from_step_order?: number;
      to_step_order: number;
      label?: string;
    }>,
  ) => {
    await onSave({ name, description, purpose, steps, connectors });
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">
            {editingFlow ? "Edit Workflow" : "Create Workflow"}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === "details" ? (
          <>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label htmlFor="workflow-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Workflow Name *
                </label>
                <input
                  id="workflow-name"
                  type="text"
                  placeholder="e.g., Customer Support Pipeline"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                  autoFocus
                />
              </div>
              <div>
                <label
                  htmlFor="workflow-description"
                  className="block text-sm font-medium text-content-secondary mb-1.5"
                >
                  Description
                </label>
                <textarea
                  id="workflow-description"
                  rows={3}
                  placeholder="Describe what this flow does..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
                />
              </div>
              <div>
                <label htmlFor="workflow-purpose" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Workflow Intent
                </label>
                <textarea
                  id="workflow-purpose"
                  rows={3}
                  placeholder="What is this workflow for? This context is passed to every step..."
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleNextToBuilder}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Next: Build Workflow
              </button>
            </div>
          </>
        ) : (
          <div className="p-6 flex-1 overflow-hidden">
            <FlowBuilder
              initialSteps={editingFlow?.steps.map((s) => ({
                id: s.id,
                agentUuid: s.agentUuid,
                agentName: s.agentName,
                config: s.config,
                requiresReview: s.requiresReview,
              }))}
              initialConnectors={editingFlow?.connectors?.map((c) => ({
                fromStepId: c.fromStepId,
                toStepId: c.toStepId,
              }))}
              onSave={handleSaveFlow}
              onCancel={onCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
}
